<template>
  <div 
    class="error-state"
    role="alert"
    aria-live="polite"
  >
    <div class="error-content">
      <div 
        v-if="showImage"
        class="error-illustration responsive-image-error-state"
        aria-hidden="true"
      >
        <img :src="resolvedImageSrc" :alt="resolvedImageAlt" loading="lazy" />
      </div>
      <div class="error-text-content" :aria-labelledby="resolvedTitle && resolvedTitle.trim() ? 'error-title' : undefined">
        <h2 v-if="resolvedTitle && resolvedTitle.trim()" id="error-title" class="error-title">{{ resolvedTitle }}</h2>
        <p v-if="message" class="custom-custom-error-message">{{ message }}</p>
        <div v-if="actions.length > 0" class="error-actions">
          <BaseButton
            v-for="(action, index) in actions"
            :key="index"
            :variant="action.variant || 'primary'"
            :class="['error-button', action.class]"
            @click="handleAction(action)"
          >
            <v-icon 
              v-if="action.icon" 
              :icon="action.icon" 
              start 
              aria-hidden="true" 
            />
            {{ action.label }}
          </BaseButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import BaseButton from "@/components/common/BaseButton.vue";
import { defineProps, defineEmits, computed } from "vue";

const props = defineProps({
  title: {
    type: String,
    default: "",
  },

  message: {
    type: String,
    default: "",
  },

  showImage: {
    type: Boolean,
    default: true,
  },

  imageSrc: {
    type: String,
    default: "",
  },

  imageAlt: {
    type: String,
    default: "Error illustration",
  },

  // 'general' (default), 'unexpected', 'notFound'
  state: {
    type: String,
    default: "general",
    validator: (value) => ["general", "unexpected", "notFound"].includes(value),
  },

  // Optional error code. If provided and `state` is not provided, the component
  // will infer a state based on this code.
  errorCode: {
    type: [Number, String],
    default: undefined,
  },

  actions: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["action"]);

const imageByState = {
  general: "/images/errors/general-error.png",
  unexpected: "/images/errors/unexpected-error.png",
  notFound: "/images/errors/not-yet-blue.svg",
};

const defaultTitleByState = {
  general: "Something went wrong",
  unexpected: "Unexpected error",
  notFound: "No results found",
};

function mapCodeToState(code) {
  const numeric = typeof code === "string" ? parseInt(code, 10) : code;
  if (numeric === 404) return "notFound";
  if (typeof numeric === "number" && numeric >= 500) return "unexpected";
  if (typeof numeric === "number" && numeric >= 400) return "general";
  return "general";
}

const resolvedState = computed(() => {
  if (props && props.state) return props.state;
  if (props && props.errorCode !== undefined && props.errorCode !== null) {
    return mapCodeToState(props.errorCode);
  }
  return "general";
});

const resolvedImageSrc = computed(() => {
  // If explicit imageSrc provided, use it; else map from resolved state
  return props && props.imageSrc
    ? props.imageSrc
    : imageByState[resolvedState.value];
});

const resolvedTitle = computed(() => {
  if (props && props.title) return props.title;
  const currentState = resolvedState.value;
  return defaultTitleByState[currentState] || defaultTitleByState.general;
});

const resolvedImageAlt = computed(() => {
  if (props && props.imageAlt && props.imageAlt !== "Error illustration") {
    return props.imageAlt;
  }
  const statusAlt = {
    general: "Something went wrong",
    unexpected: "An unexpected error occurred",
    notFound: "No results found matching your criteria",
  };
  return statusAlt[resolvedState.value] || statusAlt.general;
});

/**
 * Handle action button clicks
 */
const handleAction = (action) => {
  if (action.handler && typeof action.handler === "function") {
    action.handler();
  }

  // Emit the action for parent component handling
  emit("action", {
    type: action.type || action.label,
    action: action,
  });
};
</script>

<style scoped>
.error-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  padding: 40px 20px;
}

.error-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 600px;
  width: 100%;
}

.error-illustration {
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 500px;
  width: 100%;
}

.error-illustration img {
  width: 100%;
  height: auto;
  object-fit: contain;
}

.error-text-content {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.error-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--primary-blue);
  margin: 0;
  margin-top: 30px;
  line-height: 1.2;
}

.custom-custom-error-message {
  font-size: 1rem;
  color: var(--primary-blue);
  margin: 0;
  line-height: 1.5;
  max-width: 400px;
  font-weight: 500;
}

.error-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
  align-items: center;
}

.error-actions.multiple {
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
}

.error-button {
  min-width: 140px;
}

@media (max-width: 768px) {
  .error-state {
    min-height: 50vh;
    padding: 30px 15px;
  }

  .error-illustration {
    max-width: 250px;
    margin-bottom: 24px;
  }

  .error-title {
    font-size: 1.6rem;
  }

  .custom-custom-error-message {
    font-size: 1rem;
  }

  .error-actions {
    gap: 10px;
  }

  .error-actions.multiple {
    flex-direction: column;
  }
}

@media (max-width: 480px) {
  .error-title {
    font-size: 1.4rem;
  }

  .custom-custom-error-message {
    font-size: 0.95rem;
  }

  .error-button {
    min-width: 120px;
    font-size: 0.9rem;
  }
}

.responsive-image-error-state {
  height: 550px;
}

.white-backdrop-mini {
  color: white;
  font-size: 3em;
}

@media (max-width: 2311px) {
  .responsive-image-error-state {
    height: 550px;
  }
}

@media (max-width: 1964px) {
  .responsive-image-error-state {
    height: 380px;
  }

  .white-backdrop-mini {
    color: white;
    font-size: 1.5em;
  }
}

@media (max-width: 1307px) {
  .responsive-image-error-state {
    height: 350px;
  }
}

@media (max-width: 1206px) {
  .responsive-image-error-state {
    height: 300px;
  }

  .white-backdrop-mini {
    color: white;
    font-size: 1.4em;
  }
}

@media (max-width: 1133px) {
  .responsive-image-error-state {
    height: 300px;
  }
}

@media (max-width: 1025px) {
  .responsive-image-error-state {
    height: 250px;
  }
}

@media (max-width: 898px) {
  .responsive-image-error-state {
    height: 250px;
  }
}

@media (max-width: 768px) {
  .responsive-image-error-state {
    height: 160px;
  }
}

@media (max-width: 540px) {
  .responsive-image-error-state {
    height: 250px;
  }

  .white-backdrop-mini {
    color: white;
    font-size: 2em;
  }
}

@media (max-width: 360px) {
  .responsive-image-error-state {
    height: 250px;
  }
}
</style>
